import React, { useState } from 'react';
import { ArrowRight, CalendarClock, Loader2 } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Sterne, schnitt } from './BewerbungNotizen';

export const PHASEN = [['neu', 'Neu'], ['pruefung', 'In Prüfung'], ['eingeladen', 'Eingeladen'], ['kennenlernen', 'Kennenlernen'], ['zusage', 'Zusage'], ['absage', 'Absage']];
const STATUS_PHASE = { neu: 'neu', in_bearbeitung: 'pruefung', kontaktiert: 'eingeladen', abgesagt: 'absage', eingestellt: 'zusage' };
const FARBE = { neu: '#7a8a99', pruefung: '#34536b', eingeladen: '#2A5280', kennenlernen: '#b8893d', zusage: '#047857', absage: '#be123c' };

export const phaseVon = (b) => {
  if (b.absage) return 'absage';
  if (b.phase) return b.phase;
  if (b.kennenlernen?.status === 'geplant') return 'kennenlernen';
  if (b.status === 'kontaktiert' || (b.einladungen || []).length) return 'eingeladen';
  return STATUS_PHASE[b.status] || 'neu';
};
const datum = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return ''; } };

const Karte = ({ b, onOpen, onMove, busy }) => {
  const avg = schnitt(b.notizen);
  const k = b.kennenlernen?.status === 'geplant' ? b.kennenlernen : null;
  return (
    <div draggable={!b.absage} onDragStart={(e) => e.dataTransfer.setData('text/plain', b.bewerbung_id)} onClick={() => onOpen(b.bewerbung_id)}
      className={`select-none bg-white border border-gray-200 rounded-sm p-3 shadow-sm hover:shadow-md hover:border-[#b8893d]/50 transition-[box-shadow,border-color] duration-200 ${b.absage ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
      data-testid={`phase-karte-${b.bewerbung_id}`}>
      <div className="flex items-start gap-2">
        <p className="flex-1 text-sm font-semibold text-[#1B3A5C] leading-tight">{b.name}</p>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#b8893d]" />}
      </div>
      <p className="text-[11px] text-[#34536b] mt-0.5 truncate">{b.position || 'Initiativbewerbung'}</p>
      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-500">
        <span>{datum(b.created_at)}</span>
        {avg && <span className="inline-flex items-center gap-1"><Sterne wert={avg} klein /> Ø {avg.toLocaleString('de-DE')}</span>}
      </div>
      {k && <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[#b8893d]"><CalendarClock className="w-3 h-3" /> {k.datum.split('-').reverse().join('.')} · {k.uhrzeit} Uhr</p>}
      {!b.absage && (
        <select value={phaseVon(b)} onClick={(e) => e.stopPropagation()} onChange={(e) => onMove(b, e.target.value)} className="mt-2 w-full text-[10px] border border-gray-200 rounded-sm px-1 py-0.5 text-[#34536b] bg-white lg:hidden" data-testid={`phase-select-${b.bewerbung_id}`}>
          {PHASEN.map(([p, l]) => <option key={p} value={p}>{l}</option>)}
        </select>
      )}
    </div>
  );
};

export const phaseVerschieben = async (b, ziel, toast) => {
  if (!b || ziel === phaseVon(b)) return null;
  if (b.absage) { toast({ title: 'Bereits abgesagt', description: 'Die Phase kann nicht mehr geändert werden.', variant: 'destructive' }); return null; }
  const absage = ziel === 'absage';
  if (absage && !window.confirm(`${b.name} freundlich absagen?\nEs wird sofort eine wertschätzende Absage per E-Mail gesendet.`)) return null;
  try {
    const { data } = absage ? await api.post(`/admin/anfragen/bewerbungen/${b.bewerbung_id}/absagen`) : await api.patch(`/admin/anfragen/bewerbungen/${b.bewerbung_id}/phase`, { phase: ziel });
    toast({ title: absage ? 'Absage gesendet – Kopie an die Kanzlei' : `Verschoben nach „${PHASEN.find(([p]) => p === ziel)[1]}“` });
    return data;
  } catch (e) { toast({ title: 'Verschieben fehlgeschlagen', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); return null; }
};

export const PhaseWahl = ({ b, toast, onDone }) => {
  const [busy, setBusy] = useState(false);
  const p = phaseVon(b);
  const i = PHASEN.findIndex(([k]) => k === p);
  const naechste = !b.absage && i < 4 ? PHASEN[i + 1] : null;
  const go = async (ziel) => { setBusy(true); const d = await phaseVerschieben(b, ziel, toast); setBusy(false); if (d) onDone(d); };
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`phase-wahl-${b.bewerbung_id}`}>
      <span className="w-2 h-2 rounded-full" style={{ background: FARBE[p] }} />
      <select value={p} disabled={busy || !!b.absage} onChange={(e) => go(e.target.value)} aria-label="Phase" className="h-8 rounded-[10px] border border-gray-200 bg-white px-2 text-xs text-[#1B3A5C] focus:outline-none focus:border-[#b8893d] disabled:opacity-60" data-testid={`phase-wahl-select-${b.bewerbung_id}`}>
        {PHASEN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {naechste && <button type="button" disabled={busy} onClick={() => go(naechste[0])} className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] rounded-[10px] border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white transition-colors duration-200 disabled:opacity-50" data-testid={`phase-weiter-${b.bewerbung_id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}{naechste[1]}</button>}
    </span>
  );
};

export const BewerbungPhasen = ({ items, toast, onDone, onOpen }) => {
  const [ueber, setUeber] = useState('');
  const [busy, setBusy] = useState('');
  const move = async (b, ziel) => {
    setBusy(b?.bewerbung_id || '');
    const data = await phaseVerschieben(b, ziel, toast);
    if (data) onDone(data, false);
    setBusy('');
  };
  const drop = (e, ziel) => { e.preventDefault(); setUeber(''); move(items.find((x) => x.bewerbung_id === e.dataTransfer.getData('text/plain')), ziel); };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="bewerbungen-phasen">
      {PHASEN.map(([p, l]) => {
        const liste = items.filter((b) => phaseVon(b) === p);
        return (
          <section key={p} onDragOver={(e) => { e.preventDefault(); setUeber(p); }} onDragLeave={() => setUeber((x) => (x === p ? '' : x))} onDrop={(e) => drop(e, p)}
            className={`rounded-sm p-2.5 min-h-[180px] transition-colors duration-200 ${ueber === p ? 'bg-[#b8893d]/10 ring-2 ring-[#b8893d]/50' : 'bg-[#eef2f5]'}`} data-testid={`phase-spalte-${p}`}>
            <header className="flex items-center gap-2 mb-2.5 px-0.5 select-none">
              <span className="w-2 h-2 rounded-full" style={{ background: FARBE[p] }} />
              <h3 className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#1B3A5C] flex-1">{l}</h3>
              <span className="text-[10px] font-mono text-[#34536b] bg-white rounded-full px-1.5" data-testid={`phase-anzahl-${p}`}>{liste.length}</span>
            </header>
            <div className="space-y-2">
              {liste.map((b) => <Karte key={b.bewerbung_id} b={b} onOpen={onOpen} onMove={move} busy={busy === b.bewerbung_id} />)}
              {liste.length === 0 && <p className="text-[10px] text-gray-400 text-center py-6 select-none">Hierher ziehen</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
};
